import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import * as Actions from 'app/store/actions';
import moment from 'moment';
import React, { useContext } from 'react';
import { FaClipboardCheck } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { CompraContext } from '../../CompraContext';
import CompraFab from '../CompraFab';
import { compraModais } from '../modal/compraModais';

const getCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value).replace('R$', '');

const CompraTable = ({ onSelection, fetchCompras, fetchCompra, onDelete, onVisualizarPedido, onTour }) => {
  const [compras] = useContext(CompraContext).compras;
  const [selecao] = useContext(CompraContext).selecao;
  const [loading] = useContext(CompraContext).loading;
  const [, setModal] = useContext(CompraContext).modal;
  const [totalElements] = useContext(CompraContext).totalElements;
  const [search] = useContext(CompraContext).search;
  const [tipoSelecao, setTipoSelecao] = useContext(CompraContext).tipoSelecao;
  const [, setPage] = useContext(CompraContext).page;
  const [, setSize] = useContext(CompraContext).size;
  const [, setSort] = useContext(CompraContext).sort;
  const [filtroAvancado] = useContext(CompraContext).filtroAvancado;

  const handleOpenModalVisualizar = async (compra) => {
    await fetchCompra(compra.id);
    setModal(compraModais.visualizarCompra);
  };

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const handleOpenModalEditar = async (compra) => {
    const compraCompleta = await fetchCompra(compra.id);

    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('COMPRAS_PAGAMENTO', compraCompleta.dataCompra);
    if (response.data && response.data.bloquearAnteriores) {
      if (
        moment(compraCompleta.dataCompra).isSameOrBefore(response.data.periodo) &&
        response.data.funcionalidades.includes('COMPRAS_PAGAMENTO')
      ) {
        if (compraCompleta.compraServico) {
          throw apiUtils.handleApiError({
            message:
              'Não é possível lançar, editar e excluir compras com datas referentes a um período já fechado no sistema.',
          });
        } else {
          throw apiUtils.handleApiError({
            message:
              'A compra de produto não pode ser editada porque a data da compra refere-se a um período já fechado no sistema e não pode haver movimentações de estoque.',
          });
        }
      }
    }

    if (compraCompleta?.comprasProdutos) {
      setModal(compraModais.comprarProduto);
    } else {
      setModal(compraModais.comprarServico);
    }
  };

  const compraColumnDefs = [
    {
      title: 'Data da compra',
      name: 'dataCompra',
      getCellValue: (compra) => {
        return compra?.dataCompra ? moment(compra.dataCompra).format('DD/MM/YYYY') : '';
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      title: 'Identificador',
      name: 'compraId',
      getCellValue: (compra) => {
        return `${compra?.compraId || ''}`.padStart(3, '0');
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      title: 'Fornecedor',
      name: 'fornecedor.pessoa.nome',
      getCellValue: (compra) => {
        return compra?.fornecedor?.pessoa?.nome;
      },
      sortingEnabled: true,
    },
    {
      title: 'Vencimento',
      name: 'vencimento',
      getCellValue: (compra) => {
        return compra?.vencimento ? moment(compra?.vencimento).format('DD/MM/YYYY') : '-';
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      title: 'Condição de pagamento',
      name: 'condicaoPagamento',
      hideTooltip: true,
      sortingEnabled: true,
      width: 210,
    },
    {
      title: 'Valor (R$)',
      name: 'valorTotal',
      getCellValue: (compra) => {
        return getCurrency(compra?.valorTotal);
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'left',
      width: 125,
      getCellValue: (compra) => (
        <>
          <TableActionContainer onTour={onTour}>
            {permissaoUtils.compra().editar && (
              <TableActionButtonEdit onClick={() => handleOpenModalEditar(compra)} className={'stepIconeEditar'} />
            )}
            {permissaoUtils.compra().excluir && (
              <TableActionButtonDelete
                onClick={() => onDelete({ ids: [compra], q: search, tipoSelecao: 'NENHUM' })}
                className={'stepIconeExcluir'}
              />
            )}
            <TableActionButton
              onClick={() => onVisualizarPedido(compra)}
              title="Emitir pedido de compras"
              icon={<FaClipboardCheck size={10} className={'stepIconeEmissao'} />}
            />
          </TableActionContainer>
        </>
      ),
    },
  ];

  return (
    <>
      <SimpleTable
        stickyHeader
        params={filtroAvancado}
        contemFiltroAvancado
        getRowId={(row) => row}
        columns={compraColumnDefs}
        rows={compras}
        fetchData={fetchCompras}
        loading={loading}
        onRowClick={handleOpenModalVisualizar}
        defaultSorting={[{ columnName: 'dataCompra', direction: 'desc' }]}
        totalCount={totalElements}
        onCurrentPageChange={(params) => {
          setPage(params);
        }}
        onPageSizeChange={(params) => {
          setSize(params);
        }}
        selectionType={tipoSelecao}
        onSelectionTypeChange={(params) => {
          setTipoSelecao(params);
        }}
        // fetchOnCreate
        selection={selecao}
        onSelectionChange={onSelection}
        onSortingChange={(params) => setSort(params)}
        onLoadingChange={(params) => console.log('loading change', params)}
        searchBy={search}
      />
      {(permissaoUtils.compra().cadastrar || permissaoUtils.compra().excluir) && (
        <CompraFab selected={selecao.length} onDelete={onDelete} />
      )}
    </>
  );
};

export default CompraTable;
